// frontend/src/components/dashboard/DashboardMasthead.js
//
// The first thing a site manager sees in the morning.
//
// It used to say "Weekly Operations Overview — live priorities, performance and
// completion status for your site", which is a description of a page rather
// than a statement about the business. Nothing in it was specific to the day,
// the site or the work, so the eye skipped it and started hunting three
// sections down for whether yesterday had been filed.
//
// This says the four things that are actually true right now: which forecourt,
// what day it is, which day is being filed, and whether that day is done. The
// green is the company's, as on every report and every email the same people
// read, so the page opens looking like the rest of the system.

import React from 'react';

import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';

const DEEP = '#04301C';
const MID = '#07883f';

/** How a state reads at a glance, without needing the words. */
const TONES = {
  done: { dot: '#6ee7a8', ink: '#eafff3' },
  doing: { dot: '#fbbf24', ink: '#fff6e2' },
  todo: { dot: '#fca5a5', ink: '#ffeceb' },
};

/**
 * One fact about the day, as a dot and a phrase.
 *
 * Deliberately not a button. These report; the panel underneath is where the
 * work is started, and two places to click the same job is how the old page
 * ended up saying everything three times.
 */
function StatusChip({ tone = 'todo', label }) {
  const { dot, ink } = TONES[tone] || TONES.todo;

  return (
    <Stack
      direction="row"
      spacing={0.9}
      alignItems="center"
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        background: 'rgba(255,255,255,.10)',
        border: '1px solid rgba(255,255,255,.18)',
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: dot, flex: 'none' }} />
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ink, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function DashboardMasthead({
  siteName,
  siteCode,
  todayLabel,
  dueLabel,
  statuses = [],
  weekLabel,
  weekStart,
  onWeekStep,
  onWeekPick,
  onRefresh,
  refreshing = false,
}) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        background: `linear-gradient(115deg, ${DEEP} 0%, #06663a 55%, ${MID} 140%)`,
        color: '#fff',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 2.5 },
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.1,
              textTransform: 'uppercase',
              color: '#9fd3b4',
            }}
          >
            {siteName || 'Site'}
            {siteCode ? ` · ${siteCode}` : ''}
          </Typography>

          <Typography
            sx={{ fontSize: { xs: 22, md: 27 }, fontWeight: 950, lineHeight: 1.12, pt: 0.25 }}
          >
            {todayLabel}
          </Typography>

          {/*
            The day being worked on, said plainly.

            Sites file a day behind, so the date on the screen is almost never
            today's — and the single most common mistake on this system is
            filing against the wrong day.
          */}
          <Typography sx={{ fontSize: 14.5, color: '#cfe6d8', pt: 0.35 }}>
            Filing <strong style={{ color: '#fff' }}>{dueLabel}</strong>
          </Typography>

          {statuses.length ? (
            <Stack direction="row" spacing={1} sx={{ pt: 1.25 }} flexWrap="wrap" useFlexGap>
              {statuses.map((status) => (
                <StatusChip key={status.label} tone={status.tone} label={status.label} />
              ))}
            </Stack>
          ) : null}
        </Box>

        {/* The week on show, and how to move it. */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            flexShrink: 0,
            borderRadius: 2,
            px: 0.5,
            py: 0.5,
            background: 'rgba(255,255,255,.10)',
            border: '1px solid rgba(255,255,255,.18)',
          }}
        >
          <IconButton
            size="small"
            onClick={() => onWeekStep?.(-7)}
            sx={{ color: '#fff' }}
            aria-label="Previous week"
          >
            <ChevronLeftRounded fontSize="small" />
          </IconButton>

          {/*
            The label is the picker.

            A bare <input type="date"> rendered its own "14/09/2026" and a
            browser calendar glyph directly under the week range, so the header
            carried the same week twice — once written the way people say it and
            once in the browser's format, next to an icon nobody styled. The
            input is still here and still reachable by keyboard; it is simply
            laid over the label at zero opacity, so clicking the week opens the
            calendar.
          */}
          <Box sx={{ position: 'relative', px: 1, textAlign: 'center', minWidth: 132 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap' }}>
              {weekLabel}
            </Typography>

            <Typography sx={{ fontSize: 10.5, color: '#9fd3b4', lineHeight: 1.2 }}>
              Change week
            </Typography>

            <Box
              component="input"
              type="date"
              value={weekStart || ''}
              onChange={(event) => onWeekPick?.(event.target.value)}
              aria-label="Week beginning"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                border: 'none',
                cursor: 'pointer',
                /* Keyboard users still need to see where they are. */
                '&:focus-visible': {
                  opacity: 1,
                  background: '#04301C',
                  color: '#fff',
                  borderRadius: 1,
                },
              }}
            />
          </Box>

          <IconButton
            size="small"
            onClick={() => onWeekStep?.(7)}
            sx={{ color: '#fff' }}
            aria-label="Next week"
          >
            <ChevronRightRounded fontSize="small" />
          </IconButton>

          <Tooltip title="Reload">
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={refreshing}
                sx={{ color: '#cfe6d8' }}
                aria-label="Reload"
              >
                <RefreshRounded
                  fontSize="small"
                  sx={refreshing ? { animation: 'spin 1s linear infinite' } : undefined}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          '@keyframes spin': { from: { transform: 'rotate(0)' }, to: { transform: 'rotate(360deg)' } },
        }}
      />
    </Box>
  );
}

export { StatusChip };
